import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Product from "./Card";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import KeyFeatures from "./detailfeatures/KeyFeatures";
import ProductCard from "./Card";
import { Col, Row } from "react-bootstrap";
import { Heart, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError } from "../../utils/toastUtils";

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);

  const thumbRef = useRef();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  const [showScrollIcon, setShowScrollIcon] = useState(false);

  const [viewFixVideo, setViewFixVideo] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");
  const [stock, setStock] = useState("");

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);

        setIsWishlisted(isWishlisted || false);

        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        // default variant
        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        // images
        const images = [];
        if (product?.thumbnail) {
          images.push(product.thumbnail);
        }

        if (detail?.images?.length) {
          images.push(...detail.images);
        }

        if (variants?.length) {
          variants.forEach((v) => {
            if (v.images?.length) {
              images.push(...v.images);
            }
          });
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [slug]);

  /******************* IMAGES *******************/
  const mergedImages = [
    ...(product?.thumbnail ? [product.thumbnail] : []),
    ...(detail?.images?.map((i) => i) || []),
    ...variants.flatMap((v) => v.images || []),
  ];

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};

  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, value]) => {
      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }

      attributeMap[key].add(value);
    });
  });

  const handleVariantClick = (key, value) => {
    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(([k, val]) => v.attributes?.[k] === val),
    );

    if (matched) {
      setSelectedVariant(matched);

      if (matched.images?.length) {
        const variantImgs = matched.images;

        setDisplayedImages(variantImgs);
        setMainImage(variantImgs[0]);
      }
    }
  };

  // Create attribute map
  if (variants) {
    variants.forEach((variant) => {
      if (variant.attributes && typeof variant.attributes === "object") {
        Object.entries(variant.attributes).forEach(([key, value]) => {
          if (!attributeMap[key]) {
            attributeMap[key] = new Set();
          }
          attributeMap[key].add(value);
        });
      }
    });
  }

  // Set default attributes on mount
  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) {
        defaultSelected[key] = values[0];
      }
    });

    setSelectedAttributes(defaultSelected);
  }, []);

  const extractYouTubeID = (url) => {
    const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  // to fix bottom
  useEffect(() => {
    const handleScroll = () => {
      const quantityEl = document.querySelector(".action-btn-detail");
      const stickyEl = document.querySelector(".tf-sticky-btn-atc");
      const floatingVideo = document.querySelector(".floating-animate");

      if (!quantityEl || !stickyEl || !floatingVideo) return;

      const quantityBottom = quantityEl.getBoundingClientRect().bottom;
      const windowHeight = window.innerHeight;

      if (quantityBottom <= windowHeight) {
        stickyEl.classList.remove("show");
        floatingVideo.classList.add("active");
      } else {
        stickyEl.classList.add("show");
        floatingVideo.classList.remove("active");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;
      if (el && el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preSelectedAttributes = {};

    for (const [key, value] of params.entries()) {
      preSelectedAttributes[key] = value;
    }

    if (Object.keys(preSelectedAttributes).length > 0 && variants.length > 0) {
      setSelectedAttributes(preSelectedAttributes);

      const matched = variants.find((variant) =>
        Object.entries(preSelectedAttributes).every(
          ([key, val]) => variant.attributes?.[key] === val,
        ),
      );

      if (matched) {
        setSelectedVariant(matched);
        setTitle(`${product?.title} - ${matched.variant_name}`);
        setSkuCode(matched.sku || product?.skuCode);
        setDescription(matched.description || detail?.description);

        const variantDetailImages = matched.images
          ? matched.images.map((img) => img)
          : [];

        const mergedImages = [...variantDetailImages];

        if (mergedImages.length > 0) {
          setMainImage(mergedImages[0]);
        }
      }
    }
  }, [variants]);

  /******************* CART *******************/
  const handleAddToCart = (e) => {
    const action = e.currentTarget.dataset.action;

    try {
      addToCart(product._id, quantity, selectedVariant?._id);

      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  /******************* THUMB SCROLL *******************/

  useEffect(() => {
    const checkScroll = () => {
      const el = thumbRef.current;

      if (!el) return;

      if (el.scrollHeight > el.clientHeight) {
        setShowScrollIcon(true);
      } else {
        setShowScrollIcon(false);
      }
    };

    checkScroll();

    window.addEventListener("resize", checkScroll);

    return () => window.removeEventListener("resize", checkScroll);
  }, [mergedImages]);

  /******************* LOADING *******************/
  if (!product) return <div className="container mt-5">Loading...</div>;

  /************* FUNCTIONS FOR DEFINE*****************/
  const price = selectedVariant
    ? selectedVariant.price
    : product?.productType === "Variant"
      ? detail?.pricing?.minPrice
      : detail?.pricing?.minPrice;

  const inStock = selectedVariant ? Number(selectedVariant.stock) > 0 : true;

  const handleWishlistToggle = async (e) => {
    e.preventDefault();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  return (
    <>
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      <section className="custom-detail-hero py-5">
        <div className="container">
          <div className="row g-3">
            {/* Left: Gallery */}
            <div className="col-lg-7">
              <div className="row g-3">
                <div className="col-2 d-none d-md-block">
                  <div className="d-flex flex-column gap-3">
                    {displayedImages.slice(0, 4).map((img, i) => (
                      <div
                        key={i}
                        className={`custom-thumb-box ${mainImage === img ? "active" : ""}`}
                        onClick={() => setMainImage(img)}
                      >
                        <img
                          src={img}
                          alt={`thumb-${i}`}
                          className="img-fluid"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-md-10">
                  <div className="custom-main-img-wrapper shadow-lg">
                    {mainImage && (
                      <img
                        src={mainImage}
                        alt="Main Product"
                        className=" main-img"
                      />
                    )}

                    <div className="img-overlay-tag">Luxury Edition</div>
                  </div>
                </div>
                <div className=" d-md-none d-block">
                  <div className="d-flex flex-row gap-3">
                    {displayedImages.map((img, i) => (
                      <div
                        key={i}
                        className={`custom-thumb-box ${mainImage === img ? "active" : ""}`}
                        onClick={() => setMainImage(img)}
                      >
                        <img src={img} alt={`thumb-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Sticky Info */}
            <div className="col-lg-5">
              <div className="custom-detail-sticky-info">
                <div className="d-flex align-items-center gap-2 mb-2">
                  {brand && (
                    <span>
                      <Link
                        to={`/brands/${brand?.slug}`}
                        className="rating-pill text-decoration-none text-danger bg-light border px-2 py-1 rounded-pill small"
                      >
                        {brand?.name}
                      </Link>
                    </span>
                  )}

                  {/* stock availablity */}
                  <div className="product-stock">
                    {selectedVariant && selectedVariant.stock > 0 ? (
                      <span className="text-success small fw-bold">
                        In Stock
                      </span>
                    ) : (
                      <span className="text-danger small fw-bold">
                        Out of Stock
                      </span>
                    )}
                  </div>
                </div>

                <h1 className="custom-detail-title mb-3">{title}</h1>
                <h2 className="custom-detail-price mb-4">
                  ₹{selectedVariant ? selectedVariant.price : price}
                  {selectedVariant?.mrp > selectedVariant?.price && (
                    <span className="old-price">₹{selectedVariant.mrp}</span>
                  )}
                </h2>

             
                {/* variant wise amount section end */}

                {(product.skuCode || category) && (
                  <div className="item">
                    <ul className="p-0">
                      {product.skuCode && (
                        <li>
                          <b className="fw-bold text-dark">SKU :</b> {product.skuCode}
                        </li>
                      )}
                      {category && (
                        <li>
                          <b className="fw-bold text-dark">Category :</b>{" "}
                          <Link
                            to={`/categories/${category?.slug}`}
                            className="text-decoration-none text-muted"
                          >
                            {category.name}
                          </Link>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* variant stock hurry */}
                {selectedVariant && selectedVariant.stock <= 100 && (
                  <div className="product-progress-sale">
                    <div className="title-hurry-up">
                      <span className="text-danger fw-medium">HURRY UP!</span>{" "}
                      Only{" "}
                      <span className="count">{selectedVariant.stock}</span>{" "}
                      items left!
                    </div>
                  </div>
                )}

                <hr />
                {detail?.shortDescription && (
                  <p className="custom-detail-short-desc mb-4">
                    {detail?.shortDescription || description}
                  </p>
                )}

                <div className="tf-product-variant">
                  {Object.entries(attributeMap).map(([attrKey, values]) => {
                    const valueArray = [...values];

                    return (
                      <div
                        className="variant-size variant-picker-item"
                        key={attrKey}
                      >
                        <div className="variant-picker-label">
                          {attrKey.replace("variantAttribute", "Option")}:{" "}
                          <span className="variant-picker-label-value">
                            {selectedAttributes[attrKey] || "Select"}
                          </span>
                        </div>

                        <div className="variant-picker-values">
                          {valueArray.map((value, index) => {
                            const inputId = `${attrKey}-${value}-${index}`;
                            return (
                              <label
                                key={value}
                                htmlFor={inputId}
                                className={`variant-btn check-ship w-50 ${
                                  selectedAttributes[attrKey] === value
                                    ? "active"
                                    : ""
                                }`}
                              >
                                <input
                                  type="radio"
                                  id={inputId}
                                  name={attrKey}
                                  className="tf-check-rounded"
                                  value={value}
                                  checked={
                                    selectedAttributes[attrKey] === value
                                  }
                                  onChange={() =>
                                    handleVariantClick(attrKey, value)
                                  }
                                />
                                <span className="text text-sm">
                                  <span>{value}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add to Cart Section */}
                <div className="action-container p-4 rounded-4 bg-white border shadow-sm mb-4">
                  <label className="small fw-bold mb-2 d-block text-muted">
                    Select Quantity
                  </label>
                  <div className="d-flex gap-3 mb-3">
                    <div className="qty-input-group border rounded-0 d-flex align-items-center">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          if (quantity > 1) setQuantity(quantity - 1);
                        }}
                      >
                        -
                      </button>
                      <span>{quantity}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setQuantity(quantity + 1);
                        }}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      className={` detail-wishlist-btn flex-shrink-0 ${isWishlisted ? "active" : ""}`}
                      onClick={handleWishlistToggle}
                      aria-label="Add to wishlist"
                    >
                      <Heart size={24} className="custom-product-icon me-2" />
                      Wishlist
                    </button>
                  </div>


                  <a
                    className="btn  flex-grow-1 w-100 rounded-0 fw-bold btn-add-cart"
                    href="#shoppingCartCanvas"
                    data-bs-toggle="offcanvas"
                    onClick={handleAddToCart}
                    data-action="cart"
                  >
                    <ShoppingBag size={18} className="me-2" /> Add to Cart
                  </a>
                  <a
                    href="#"
                    className=" btn rounded-0 fw-bold btn-detail text-decoration-none btn-buy-now w-100 mt-2"
                    onClick={handleAddToCart}
                    data-action="buy-now"
                  >
                    Buy it now
                  </a>
                </div>

                {detail.video && viewFixVideo && (
                  <>
                    {detail.video.includes("youtube.com") ||
                    detail.video.includes("youtu.be") ? (
                      <iframe
                        width="100%"
                        height="450"
                        src={`https://www.youtube.com/embed/${extractYouTubeID(
                          detail.video,
                        )}`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title="YouTube video"
                      ></iframe>
                    ) : (
                      <video width="100%" height="450" controls autoPlay muted>
                        <source src={detail.video} type="video/mp4" />
                        Your browser does not support HTML5 video.
                      </video>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pf-wrap py-5">
        <div className="container">
          <h2 className="custom-feature-heading">Product highlights</h2>


          {detail?.keyFeatures?.length > 0 && (
            <div className="spec-table rounded-4 border overflow-hidden">
              {detail.keyFeatures.map((feature, index) => (
                <div className="row g-0 border-bottom" key={index}>
                  <div className="col-4 p-3 bg-light fw-bold feature-label">{feature?.label}</div>
                  <div className="col-8 p-3 feature-value">{feature?.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="custom-desc-section pb-5">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="custom-desc-heading mb-4">Product Description</h2>
              <div
                className="custom-desc-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        </div>
      </section>

      {relatedProducts && relatedProducts.length > 0 && (
        <div className="related-products-section bg-light py-5">
          <div className="container product">
            <div className="flat-animate-tab flat-controltab-nav">
              <div className="text-start mb-4">
                <h4 className="section-title">Related Products</h4>
              </div>
              <div className="row">
                {relatedProducts.map((product, index) => (
                  <div
                    className="col-lg-3 col-md-4 col-12 mb-2"
                    key={product?._id || index}
                  >
                    <Product item={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DetailPage;
